import * as _ from 'lodash';

import Locator from '../../../../../shared/Locator';
import { useMutation } from '../../../../ReactQuery';
import { post, postCancellable } from '../../../../redux/api';
import { nuc } from '../../../../utils';
import type { CheckRevisionResponse } from '../../../../../shared/revisions/types';

export const GIT_CHECK_REVISION_TIMEOUT_MS = 45_000;

type CheckRevisionTimedOut = {
  kind: 'timeout';
};

export type CheckRevisionResult = CheckRevisionResponse | CheckRevisionTimedOut;

/**
 * Return true when a revision check ended in a client-side timeout.
 */
export function isCheckRevisionTimedOut(result?: CheckRevisionResult): result is CheckRevisionTimedOut {
  return !!result && 'kind' in result && result.kind === 'timeout';
}

function isAbortError(error: unknown): error is Error {
  return _.isError(error) && error.name === 'AbortError';
}

/**
 * Resolve a revision via the FOSSA backend.
 *
 * Non-Git fetchers resolve quickly server-side, so we use a plain POST.
 *
 * Git fetchers can take much longer (clone + lookup), so we bound the wait
 * with a client-side timeout backed by `AbortController`. AbortSignal is the
 * standard browser primitive for cancelling an in-flight `fetch`, but it is
 * not used anywhere else in the app yet — see the inline comments below for
 * the cancellation flow.
 */
const checkRevision = async (revisionLocator: string): Promise<CheckRevisionResult | undefined> => {
  const url = `/api/revisions/${nuc(revisionLocator)}/check`;
  const locator = new Locator(revisionLocator);

  if (locator.fetcher.value !== 'git') {
    const resp = await post<unknown, CheckRevisionResponse>(url, {});
    return resp.data;
  }

  // `controller.signal` is forwarded into the underlying `fetch` via
  // `postCancellable`. The `setTimeout` calls `controller.abort()` once the
  // deadline elapses, which causes the in-flight request to reject with an
  // `AbortError` rather than hanging indefinitely.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), GIT_CHECK_REVISION_TIMEOUT_MS);

  try {
    const resp = await postCancellable<unknown, CheckRevisionResponse>(url, {}, { signal: controller.signal });
    return resp.data;
  } catch (error) {
    // An aborted fetch surfaces here as an `AbortError`. We translate it into
    // a structured `{ kind: 'timeout' }` sentinel so callers can render the
    // indeterminate UI instead of treating the timeout as a hard failure.
    if (isAbortError(error)) {
      return { kind: 'timeout' };
    }

    throw error;
  } finally {
    // Always clear the timeout — on the success/error paths the request has
    // already settled, so we must not let the timer fire later and abort an
    // already-completed controller.
    clearTimeout(timeoutId);
  }
};

/**
 * Hook for checking if a revision is likely to resolve when added as a dependency.
 */
export default function useCheckRevision() {
  return useMutation<string, CheckRevisionResult | undefined>({
    mutationFn: async (revisionLocator: string) => checkRevision(revisionLocator),
  });
}
